/**
 * Unteraufgaben einer Hauptaufgabe (Checkliste) bzw. Verweis auf die Hauptaufgabe – mit Mehrfachauswahl, Sammel-Abhaken und
 * Kommunikationsentwürfen. Brief / E-Mail / Fax öffnen einen vorbelegten Entwurf in der Korrespondenz (dort Bearbeiten, Vorschau,
 * Versand erst nach Bestätigung, Sendelog, Ablage in der Akte). WhatsApp / Kurznachricht: nur Text zum Kopieren – kein Versand.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Check, CheckCheck, ListChecks, MailPlus, Copy, CornerLeftUp, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { betreuungAPI } from '@/api/betreuungAPI';
import { useTabs } from '@/contexts/TabContext';
import { fortschritt, kurzTitel } from '@/lib/aufgabenBaum';
import { schreibstubeUrl } from '@/pages/schreibstube/schreibstube';

// schreibstube: true -> Entwurf in der Schreibstube (echter Versandweg dort); sonst nur Text zum Kopieren
export const ENTWURF_KANAELE = [
  { kanal: 'brief', label: 'Brief (Korrespondenz)', schreibstube: true },
  { kanal: 'email', label: 'E-Mail (Korrespondenz)', schreibstube: true },
  { kanal: 'fax', label: 'Fax (Korrespondenz)', schreibstube: true },
  { kanal: 'kurznachricht', label: 'WhatsApp / Kurznachricht (Text kopieren)' },
];


function EntwurfMenue({ aufgabe, onEntwurf }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-blue-700" data-testid={`entwurf-menu-${aufgabe.id}`}>
          <MailPlus className="w-3.5 h-3.5 mr-1" /> Entwurf
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {ENTWURF_KANAELE.map(k => (
          <DropdownMenuItem key={k.kanal} onClick={() => onEntwurf(aufgabe, k.kanal)} data-testid={`entwurf-${k.kanal}-${aufgabe.id}`}>
            {k.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function UnteraufgabenKarte({ aufgabe, onChanged }) {
  const { openTab } = useTabs();
  const [kinder, setKinder] = useState([]);
  const [eltern, setEltern] = useState(null);
  const [auswahl, setAuswahl] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [entwurf, setEntwurf] = useState(null);

  const laden = useCallback(async () => {
    if (!aufgabe?.id) return;
    try {
      const k = await betreuungAPI.listAufgaben({ eltern_aufgabe_id: aufgabe.id });
      const reihenfolge = (aufgabe.checkliste || []).map(c => c.schluessel);
      const pos = (x) => { const i = reihenfolge.indexOf(x.checklisten_schluessel); return i < 0 ? 999 : i; };
      setKinder((Array.isArray(k) ? k : []).sort((a, b) => pos(a) - pos(b)));
    } catch { setKinder([]); }
    if (aufgabe.eltern_aufgabe_id) {
      try { setEltern(await betreuungAPI.getAufgabe(aufgabe.eltern_aufgabe_id)); } catch { setEltern(null); }
    } else setEltern(null);
  }, [aufgabe?.id, aufgabe?.eltern_aufgabe_id, aufgabe?.checkliste]);

  useEffect(() => { laden(); }, [laden]);

  const setzeStatus = async (ids, status) => {
    setBusy(true);
    try {
      await Promise.all(ids.map(id => betreuungAPI.updateAufgabe(id, { status })));
      toast.success(`${ids.length} Unteraufgabe(n) ${status === 'erledigt' ? 'erledigt' : 'wieder geöffnet'}`);
      setAuswahl(new Set());
      await laden();
      onChanged?.();
    } catch { toast.error('Aktualisieren fehlgeschlagen'); } finally { setBusy(false); }
  };

  const zeigeEntwurf = async (a, kanal) => {
    if (ENTWURF_KANAELE.find(k => k.kanal === kanal)?.schreibstube) {
      try {
        const r = await betreuungAPI.schreibstubeEntwurf(a.id, kanal);
        (r.hinweise || []).slice(1).forEach(h => toast.info(h));
        openTab(schreibstubeUrl({ brief: r.brief.id }), 'Korrespondenz');
      } catch (e) { toast.error(e.message); }
      return;
    }
    try { setEntwurf(await betreuungAPI.kommunikationsEntwurf(a.id, kanal)); } catch (e) { toast.error(e.message); }
  };

  const kopieren = async () => {
    const text = [entwurf?.betreff ? `Betreff: ${entwurf.betreff}` : null, entwurf?.text].filter(Boolean).join('\n\n');
    try { await navigator.clipboard.writeText(text); toast.success('Entwurf kopiert'); } catch { toast.error('Kopieren nicht möglich'); }
  };

  const toggle = (id) => setAuswahl(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const f = fortschritt(kinder);
  const offeneIds = kinder.filter(k => k.status !== 'erledigt').map(k => k.id);

  const dialog = (
    <Dialog open={!!entwurf} onOpenChange={(o) => !o && setEntwurf(null)}>
      <DialogContent className="max-w-2xl" data-testid="entwurf-dialog">
        <DialogHeader>
          <DialogTitle>Entwurf – {entwurf?.kanal_label}</DialogTitle>
          <DialogDescription>An: {entwurf?.empfaenger}. Nur Entwurf – es wird nichts versendet.</DialogDescription>
        </DialogHeader>
        {entwurf?.betreff && <p className="text-sm"><span className="text-slate-500">Betreff:</span> {entwurf.betreff}</p>}
        <pre className="whitespace-pre-wrap text-sm bg-slate-50 border rounded p-3 max-h-[50vh] overflow-y-auto font-sans" data-testid="entwurf-text">{entwurf?.text}</pre>
        <ul className="text-xs text-amber-700 space-y-0.5">{(entwurf?.hinweise || []).map((h, i) => <li key={i}>• {h}</li>)}</ul>
        <div className="flex justify-end"><Button size="sm" variant="outline" onClick={kopieren}><Copy className="w-4 h-4 mr-1" /> Kopieren</Button></div>
      </DialogContent>
    </Dialog>
  );

  if (!kinder.length && !eltern) {
    // Einzelne Aufgabe mit Entwurfsvorschlag (z. B. alte Unteraufgabe ohne geladene Hauptaufgabe)
    return aufgabe?.zeo_entwurf_vorschlag ? (
      <>
        <div className="flex justify-end"><EntwurfMenue aufgabe={aufgabe} onEntwurf={zeigeEntwurf} /></div>{dialog}
      </>
    ) : null;
  }

  return (
    <Card data-testid="unteraufgaben-karte">
      {eltern && (
        <CardContent className="pt-4 pb-3 flex items-center justify-between gap-2">
          <button className="text-sm text-blue-700 hover:underline flex items-center gap-1 text-left" data-testid="zur-hauptaufgabe"
            onClick={() => openTab(`/aufgaben/${eltern.id}`, kurzTitel(eltern))}>
            <CornerLeftUp className="w-4 h-4 shrink-0" /> Teil von: {kurzTitel(eltern)}
          </button>
          <EntwurfMenue aufgabe={aufgabe} onEntwurf={zeigeEntwurf} />
        </CardContent>
      )}
      {kinder.length > 0 && (
        <>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-blue-600" /> Unteraufgaben
                <Badge variant="outline" className="text-xs font-normal" data-testid="unteraufgaben-fortschritt">{f.text}</Badge>
              </CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={busy || !auswahl.size} onClick={() => setzeStatus([...auswahl], 'erledigt')} data-testid="unteraufgaben-markierte-erledigen">
                  <CheckCheck className="w-4 h-4 mr-1" /> Markierte erledigen{auswahl.size ? ` (${auswahl.size})` : ''}
                </Button>
                <Button size="sm" variant="ghost" disabled={busy || !offeneIds.length} onClick={() => setAuswahl(new Set(offeneIds))} data-testid="unteraufgaben-alle-offenen">
                  Alle offenen markieren
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-1">
            {kinder.map(k => {
              const erledigt = k.status === 'erledigt';
              const gewaehlt = auswahl.has(k.id);
              return (
                <div key={k.id} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${gewaehlt ? 'bg-blue-50' : 'hover:bg-slate-50'}`} data-testid={`unteraufgabe-${k.id}`}>
                  <button onClick={() => toggle(k.id)} aria-label="Auswählen" data-testid={`unteraufgabe-auswahl-${k.id}`}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${gewaehlt ? 'bg-blue-500 border-blue-500 text-white' : 'border-slate-300'}`}>
                    {gewaehlt && <Check className="w-3.5 h-3.5" />}
                  </button>
                  <button className={`flex-1 text-left text-sm ${erledigt ? 'line-through text-slate-400' : 'text-slate-800'}`}
                    onClick={() => openTab(`/aufgaben/${k.id}`, kurzTitel(k))}>
                    {kurzTitel(k)}
                  </button>
                  {erledigt
                    ? <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" disabled={busy} onClick={() => setzeStatus([k.id], 'offen')}><RotateCcw className="w-3.5 h-3.5 mr-1" />Öffnen</Button>
                    : <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-green-700" disabled={busy} onClick={() => setzeStatus([k.id], 'erledigt')} data-testid={`unteraufgabe-erledigen-${k.id}`}><Check className="w-3.5 h-3.5 mr-1" />Erledigt</Button>}
                  {k.zeo_entwurf_vorschlag && <EntwurfMenue aufgabe={k} onEntwurf={zeigeEntwurf} />}
                </div>
              );
            })}
          </CardContent>
        </>
      )}
      {dialog}
    </Card>
  );
}
